import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import type {
  AthleteView,
  PageResponse,
  ProgressSeriesView,
  ProgressView,
  SupportedLocale,
} from '@stm/contracts';
import { translate } from '@stm/i18n';
import { ApiRequestError, apiRequest } from './api';
import { buildChart, changeLabel, formatSeriesValue, localizedText } from './assessments';
import { athleteDisplayName, athleteListPath, formatCalendarDate } from './athletes';

type ErrorKey = ApiRequestError['messageKey'];

function errorKeyOf(error: unknown): ErrorKey {
  return error instanceof ApiRequestError ? error.messageKey : 'error.unexpected';
}

interface SeriesChartProps {
  locale: SupportedLocale;
  series: ProgressSeriesView;
}

/**
 * One comparable series as a line chart. The line is broken at a date with no valid value (marked
 * with a cross) and never dips to zero; the table below the chart carries the same data for
 * keyboard and screen-reader users.
 */
function SeriesChart({ locale, series }: SeriesChartProps): React.JSX.Element {
  const title = localizedText(series.titles, locale);
  const chart = buildChart(series);
  const axisBottom = chart.height - 28;
  const decimals = series.precision;
  const label = `${translate(locale, 'assessments.progress.chart')}: ${title}`;
  return (
    <Box
      component="svg"
      viewBox={`0 0 ${String(chart.width)} ${String(chart.height)}`}
      role="img"
      aria-label={label}
      sx={{ width: '100%', maxWidth: 640, height: 'auto', color: 'primary.main' }}
    >
      <title>{label}</title>
      <line x1={48} y1={axisBottom} x2={chart.width - 16} y2={axisBottom} stroke="#6b7280" />
      <line x1={48} y1={12} x2={48} y2={axisBottom} stroke="#6b7280" />
      <text x={44} y={20} textAnchor="end" fontSize="11" fill="#374151">
        {chart.yMaximum.toFixed(decimals)}
      </text>
      <text x={44} y={axisBottom} textAnchor="end" fontSize="11" fill="#374151">
        {chart.yMinimum.toFixed(decimals)}
      </text>
      {chart.segments.map((segment) => (
        <g key={segment[0]?.key ?? 'empty'}>
          {segment.length > 1 && (
            <polyline
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              points={segment
                .map((point) => `${String(point.horizontal)},${String(point.vertical)}`)
                .join(' ')}
            />
          )}
          {segment.map((point) => (
            <circle
              key={point.key}
              cx={point.horizontal}
              cy={point.vertical}
              r={4}
              fill="currentColor"
            >
              <title>{`${formatCalendarDate(point.date, locale)}: ${point.value}`}</title>
            </circle>
          ))}
        </g>
      ))}
      {chart.gaps.map((gap) => (
        <g key={gap.key} stroke="#b91c1c" strokeWidth={2}>
          <line
            x1={gap.horizontal - 4}
            y1={axisBottom - 8}
            x2={gap.horizontal + 4}
            y2={axisBottom}
          />
          <line
            x1={gap.horizontal + 4}
            y1={axisBottom - 8}
            x2={gap.horizontal - 4}
            y2={axisBottom}
          />
          <title>{`${formatCalendarDate(gap.date, locale)}: ${translate(locale, 'assessments.progress.noValue')}`}</title>
        </g>
      ))}
    </Box>
  );
}

function SeriesBlock({ locale, series }: SeriesChartProps): React.JSX.Element {
  const title = localizedText(series.titles, locale);
  const context = [
    series.protocol.distance,
    series.protocol.course,
    series.protocol.surface,
    series.protocol.method,
  ].filter((part): part is string => part !== null);
  return (
    <Paper variant="outlined" sx={{ padding: 2 }}>
      <Typography variant="h6" component="h3">
        {title}
      </Typography>
      {context.length > 0 && (
        <Typography variant="body2" color="text.secondary">
          {translate(locale, 'assessments.progress.context')}: {context.join(' · ')}
        </Typography>
      )}
      <SeriesChart locale={locale} series={series} />
      <Typography variant="body2" color="text.secondary">
        {translate(locale, 'assessments.progress.gapNote')}
      </Typography>
      {series.change !== null && (
        <Box sx={{ mt: 1 }}>
          <Typography sx={{ fontWeight: 600 }}>
            {translate(locale, 'assessments.progress.change')}
          </Typography>
          <Typography>
            {formatCalendarDate(series.change.baselineDate, locale)}:{' '}
            {formatSeriesValue(series.change.baselineValue, series, locale)} →{' '}
            {formatCalendarDate(series.change.latestDate, locale)}:{' '}
            {formatSeriesValue(series.change.latestValue, series, locale)}
          </Typography>
          <Typography>
            {translate(locale, 'assessments.progress.absolute')}:{' '}
            {changeLabel(series.change, series.unit)}
            {series.change.percent !== null &&
              ` · ${translate(locale, 'assessments.progress.percent')}: ${series.change.percent.toLocaleString(locale, { maximumFractionDigits: 1 })} %`}
          </Typography>
          {series.change.percent === null && (
            <Typography variant="body2" color="text.secondary">
              {translate(locale, 'assessments.progress.absoluteOnly')}
            </Typography>
          )}
          <Typography variant="body2" color="text.secondary">
            {translate(locale, 'assessments.progress.calculation')}
          </Typography>
        </Box>
      )}
      <Box sx={{ overflowX: 'auto' }}>
        <Table
          size="small"
          sx={{ mt: 1, '& td, & th': { px: 1 } }}
          aria-label={`${translate(locale, 'assessments.progress.table')}: ${title}`}
        >
          <TableHead>
            <TableRow>
              <TableCell>{translate(locale, 'assessments.progress.date')}</TableCell>
              <TableCell>{translate(locale, 'assessments.progress.value')}</TableCell>
              <TableCell>{translate(locale, 'assessments.progress.version')}</TableCell>
              <TableCell>{translate(locale, 'assessments.progress.comment')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {series.points.map((point) => (
              <TableRow
                key={`${point.sessionId}-${point.definitionVersionId}-${String(point.revisionNumber)}`}
              >
                <TableCell>{formatCalendarDate(point.sessionDate, locale)}</TableCell>
                <TableCell>{formatSeriesValue(point.value, series, locale)}</TableCell>
                <TableCell>{point.versionNumber}</TableCell>
                <TableCell>{point.publicComment ?? ''}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    </Paper>
  );
}

export interface ProgressSectionProps {
  locale: SupportedLocale;
  base: string;
  /** Opens this athlete's progress straight away (a deep link). */
  initialAthleteId?: string | undefined;
}

/**
 * Published progress of one athlete. A guardian sees the children of their current approved links,
 * an athlete their own record and a coach the athletes of their department; the server decides, and
 * only published results (never drafts, attempts or private notes) are ever sent.
 */
export function ProgressSection({
  locale,
  base,
  initialAthleteId,
}: ProgressSectionProps): React.JSX.Element {
  const [search, setSearch] = useState('');
  const [athletes, setAthletes] = useState<AthleteView[] | null>(null);
  const [athleteId, setAthleteId] = useState(initialAthleteId ?? '');
  const [progress, setProgress] = useState<ProgressView | null>(null);
  const [errorKey, setErrorKey] = useState<ErrorKey | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      apiRequest<PageResponse<AthleteView>>(athleteListPath(base, { search })).then(
        (page) => {
          setAthletes(page.items);
          // A person with exactly one child sees that child's progress without choosing.
          setAthleteId((current) =>
            current === '' && page.items.length === 1 ? (page.items[0]?.id ?? '') : current,
          );
        },
        () => {
          setAthletes([]);
        },
      );
    }, 250);
    return () => {
      clearTimeout(timer);
    };
  }, [base, search]);

  useEffect(() => {
    if (athleteId === '') {
      setProgress(null);
      return;
    }
    setErrorKey(null);
    apiRequest<ProgressView>(`${base}/athletes/${athleteId}/progress`).then(
      setProgress,
      (error: unknown) => {
        setProgress(null);
        setErrorKey(errorKeyOf(error));
      },
    );
  }, [base, athleteId]);

  return (
    <Box
      component="section"
      sx={{ mt: 4 }}
      aria-label={translate(locale, 'assessments.progress.title')}
    >
      <Typography variant="h5" component="h2" gutterBottom>
        {translate(locale, 'assessments.progress.title')}
      </Typography>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        <TextField
          size="small"
          sx={{ minWidth: { xs: '100%', sm: 200 } }}
          label={translate(locale, 'assessments.session.search')}
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
          }}
        />
        <TextField
          select
          size="small"
          sx={{ minWidth: { xs: '100%', sm: 240 } }}
          label={translate(locale, 'assessments.progress.athlete')}
          value={athletes?.some((athlete) => athlete.id === athleteId) === true ? athleteId : ''}
          onChange={(event) => {
            setAthleteId(event.target.value);
          }}
        >
          <MenuItem value="">{translate(locale, 'assessments.progress.choose')}</MenuItem>
          {athletes?.map((athlete) => (
            <MenuItem key={athlete.id} value={athlete.id}>
              {athleteDisplayName(athlete, locale)}
            </MenuItem>
          ))}
        </TextField>
      </Box>
      {athletes?.length === 0 && (
        <Typography color="text.secondary" sx={{ mt: 1 }}>
          {translate(locale, 'assessments.progress.noAthletes')}
        </Typography>
      )}
      {errorKey !== null && (
        <Alert severity="error" sx={{ mt: 1 }}>
          {translate(locale, errorKey)}
        </Alert>
      )}
      {progress?.series.length === 0 && (
        <Typography color="text.secondary" sx={{ mt: 2 }}>
          {translate(locale, 'assessments.progress.empty')}
        </Typography>
      )}
      <Stack spacing={2} sx={{ mt: 2 }}>
        {progress?.series.map((series) => (
          <SeriesBlock key={series.comparabilityKey} locale={locale} series={series} />
        ))}
      </Stack>
    </Box>
  );
}
